import { all, put, takeEvery, select, call, takeLatest } from 'redux-saga/effects';
import {
  getPortfolios,
  getPortfolioDetail,
  createApGroup,
  updateApGroupParentInfo,
  fetchFileFromS3,
} from '../../api/omakasePlanApi';
import { errorHandler } from './errorSaga';
import { OmakasePlanTermMap, OmakasePlanTerms, OmakasePlanPolicies } from '../../constants/omakasePlan';
import {
  CHANGE_CREATING_SIMULATION_TERM_ID,
  CREATE_OMAKASE_PLAN_REQUEST,
  FIND_OMAKASE_PLAN_STRATEGIES,
  GET_PORTFOLIOS_REQUEST,
  GET_PORTFOLIO_DETAIL_REQUEST,
  CREATE_AP_GROUP_REQUEST,
  CHANGE_NAME_REQUEST,
  GET_OMAKASE_PLAN_MASTER_REQUEST,
} from '../actionConstants/omakasePlanConstants';
import { NOT_FOUND_CODE } from '../../constants/apiConstant';
import { sendNotificationSuccess } from '../actions/notificationActions';
import {
  setOmakasePlanLoading,
  setOmakaseCreatingInput,
  setOmakaseCreatingResult,
  setOmakaseCreatingStrategyList,
  resetOmakaseCreatingInputSetCount,
  getPortfoliosRequestStartLoading,
  getPortfoliosRequestEndLoading,
  getPortfoliosSuccess,
  getPortfolioDetailRequestStartLoading,
  getPortfolioDetailRequestEndLoading,
  getPortfolioDetailSuccess,
  setOmakaseCreatingStrategyListCache,
  createOmakasePlanApGroupRequestStartLoading,
  createOmakasePlanApGroupRequestEndLoading,
  changeOmakasePlanNameRequestStartLoading,
  changeOmakasePlanNameRequestEndLoading,
  getOmakasePlanMasterRequestStartLoading,
  getOmakasePlanMasterRequestEndLoading,
  getOmakasePlanMasterSuccess,
  setOmakasePlanSubmitProcessing,
} from '../actions/omakasePlanActions';
import { getApGroupRequest, getApGroupByIdRequest } from '../actions/portfolioActions';
import { OmakasePlanDefaultSimulationTermMap, STRATEGY_SETS_RESULTS } from '../../constants';

function* findOmakasePlanId(term) {
  const omakasePlanList = yield select((state) => state.omakasePlan.portfolios); // 取得済み想定
  if (!omakasePlanList?.length) {
    return undefined;
  }
  const omakaseTermId = OmakasePlanTermMap[term];
  const omakasePlanId = omakasePlanList.find(
    ({ portfolioTermId, publicFlg }) => portfolioTermId === omakaseTermId && publicFlg,
  )?.portfolioId;
  return omakasePlanId;
}

const sortStrategyList = (strategyList) => {
  const getMarginRecommended = (strategy) => strategy?.strategyDetail?.simulationStats?.marginRecommended || 0;
  const getStrategyId = (strategy) => Number(strategy?.strategyId || 0);
  const compareMarginRecommended = (a, b) => getMarginRecommended(b) - getMarginRecommended(a); // 降順
  const compareStrategyId = (a, b) => getStrategyId(a) - getStrategyId(b);
  return strategyList?.sort?.((a, b) => {
    return compareMarginRecommended(a, b) || compareStrategyId(a, b);
  });
};

/**
 * #37
 * 運用資金(/推奨証拠金)は期間、セット数に応じて更新する
 * 運用資金(/推奨証拠金)を更新してもカード並び順は変更しない
 */
function* sortStrategyListDefaultOrder(strategyList, defaultSimulationTermId) {
  const cached = yield select((state) => state.omakasePlan.creating.strategyListCache[defaultSimulationTermId]);
  if (cached) {
    const findIndex = (strategy) =>
      cached.findIndex((cachedStrategy) => cachedStrategy.strategyId === strategy.strategyId);
    return strategyList?.sort?.((a, b) => {
      return findIndex(a) - findIndex(b);
    });
  }
  return sortStrategyList(strategyList);
}

// this has no errorHandler.
function* getStrategyListWithoutErrorHandler({
  omakasePlanId,
  simulationTermId,
  defaultSimulationTermId,
  selectedOmakasePlan,
}) {
  const cached = yield select((state) => state.omakasePlan.creating.strategyListCache[simulationTermId]);
  if (cached) {
    yield put(setOmakaseCreatingStrategyList({ strategyList: cached }));
    return;
  }
  try {
    const { data: portfolioDetail } = yield call(getPortfolioDetail, {
      portfolioId: omakasePlanId,
      termId: simulationTermId,
    });
    const { strategyList = [] } = portfolioDetail || {};
    const hasCountStrategyId = (strategyId) =>
      Object.entries(selectedOmakasePlan).find(([key, val]) => key === String(strategyId) && Number(val) > 0);
    const filteredStrategyList = strategyList.filter((strategy) => hasCountStrategyId(strategy.strategyId));

    if (simulationTermId === defaultSimulationTermId) {
      sortStrategyList(filteredStrategyList);
    } else {
      // 期間を変更したとき並び順が変わらないように、初回(=defaultSimulationTermId)の並び順と同じにする
      yield* sortStrategyListDefaultOrder(filteredStrategyList, defaultSimulationTermId);
    }
    yield put(setOmakaseCreatingStrategyList({ strategyList: filteredStrategyList }));
    yield put(setOmakaseCreatingStrategyListCache({ simulationTermId, strategyList: filteredStrategyList }));
  } catch (e) {
    if (e?.response?.status === NOT_FOUND_CODE) {
      yield put(setOmakaseCreatingStrategyList({ strategyList: [] }));
      yield put(setOmakaseCreatingStrategyListCache({ simulationTermId, strategyList: null }));
      return;
    }
    throw e;
  }
}

function* findOmakasePlanStrategiesHandler(action) {
  try {
    yield put(setOmakasePlanLoading({ loading: true }));
    const {
      payload: { selectedOmakasePlan },
    } = action;

    const inputForm = yield select((state) => state.omakasePlan.inputForm);
    yield put(setOmakaseCreatingInput({ input: inputForm }));
    const omakasePlanId = yield call(findOmakasePlanId, inputForm.term);
    if (omakasePlanId == null) {
      return;
    }
    const simulationTermId = OmakasePlanDefaultSimulationTermMap[inputForm.term];
    yield put(
      setOmakaseCreatingResult({
        result: { omakasePlanId, selectedOmakasePlan, defaultSimulationTermId: simulationTermId },
      }),
    );
    yield call(getStrategyListWithoutErrorHandler, {
      omakasePlanId,
      simulationTermId,
      defaultSimulationTermId: simulationTermId,
      selectedOmakasePlan,
    });
    yield put(resetOmakaseCreatingInputSetCount());
  } catch (e) {
    yield call(errorHandler, { error: e });
  } finally {
    yield put(setOmakasePlanLoading({ loading: false }));
  }
}

function* createOmakasePlanRequestHandler(action) {
  try {
    yield put(setOmakasePlanSubmitProcessing({ isProcessing: true }));
    const {
      payload: { serviceId, name, strategyList, successCallback, failureCallback },
    } = action;
    if (!serviceId || !name || !strategyList?.length) {
      throw Error('createOmakasePlanRequestHandler parameter error');
    }
    const omakasePlanId = yield select((state) => state.omakasePlan.creating.result?.omakasePlanId);
    if (omakasePlanId == null) {
      throw Error('createOmakasePlanRequestHandler omakasePlanId not found.');
    }

    const requestBody = { portfolioId: omakasePlanId, name, strategyList };
    const { data } = yield call(createApGroup, { serviceId, requestBody });
    if (data?.length) {
      const failedList = data.filter((res) => res.status === STRATEGY_SETS_RESULTS.FAIL);
      const succeededList = data.filter((res) => res.status === STRATEGY_SETS_RESULTS.SUCCESS);
      if (failedList.length === data.length) {
        failureCallback?.({ type: 'all', failedList, succeededList });
      } else if (failedList.length === 0) {
        successCallback?.();
      } else {
        failureCallback?.({ type: 'partial', failedList, succeededList });
      }
    } else {
      throw Error('createOmakasePlanRequestHandler api response error');
    }
  } catch (e) {
    yield call(errorHandler, { error: e });
  } finally {
    yield put(setOmakasePlanSubmitProcessing({ isProcessing: false }));
  }
}

function* getPortfoliosRequestHandler() {
  try {
    yield put(getPortfoliosRequestStartLoading());

    const { data: portfolios } = yield call(getPortfolios);
    yield put(getPortfoliosSuccess({ portfolios }));
  } catch (e) {
    yield call(errorHandler, { error: e });
  } finally {
    yield put(getPortfoliosRequestEndLoading());
  }
}

function* getPortfolioDetailDataRequestHandler(action) {
  try {
    yield put(getPortfolioDetailRequestStartLoading());
    const {
      payload: { portfolioId, termId },
    } = action;

    const { data: portfolioDetail } = yield call(getPortfolioDetail, { portfolioId, termId });

    yield put(getPortfolioDetailSuccess({ portfolioDetail }));
  } catch (e) {
    yield call(errorHandler, { error: e });
  } finally {
    yield put(getPortfolioDetailRequestEndLoading());
  }
}

function* changeSimulationTermIdHandler(action) {
  try {
    yield put(setOmakasePlanLoading({ loading: true }));
    const {
      payload: { simulationTermId },
    } = action;

    const input = yield select((state) => state.omakasePlan.creating.input);
    const selectedOmakasePlan = yield select((state) => state.omakasePlan.creating.result.selectedOmakasePlan);
    const omakasePlanId = yield call(findOmakasePlanId, input.term);
    if (!omakasePlanId) {
      return;
    }
    const defaultSimulationTermId = yield select((state) => state.omakasePlan.creating.result.defaultSimulationTermId);
    yield call(getStrategyListWithoutErrorHandler, {
      omakasePlanId,
      simulationTermId,
      defaultSimulationTermId,
      selectedOmakasePlan,
    });
  } catch (e) {
    yield call(errorHandler, { error: e });
  } finally {
    yield put(setOmakasePlanLoading({ loading: false }));
  }
}

function* createOmakasePlanApGroupRequestHandler(action) {
  try {
    const {
      payload: { callback },
    } = action;

    const serviceId = yield select((state) => state.auth.serviceId);

    yield put(createOmakasePlanApGroupRequestStartLoading());

    const {
      payload: { portfolioId, name, strategyList },
    } = action;

    const requestBody = { portfolioId, name, strategyList };

    const { data } = yield call(createApGroup, { serviceId, requestBody });

    if (callback) callback(data);
  } catch (e) {
    yield call(errorHandler, { error: e, buttonText: 'OK' });
  } finally {
    yield put(createOmakasePlanApGroupRequestEndLoading());
  }
}

function* changeOmakasePlanNameRequestHandler(action) {
  try {
    yield put(changeOmakasePlanNameRequestStartLoading());

    const {
      payload: { parentInfoId, parentName, serviceId, groupId },
    } = action;

    const requestBody = { parentInfoId, parentName };

    yield call(updateApGroupParentInfo, { requestBody });

    yield put(getApGroupByIdRequest({ id: groupId, serviceId }));
    yield put(getApGroupRequest({ serviceId }));

    yield put(sendNotificationSuccess({ message: '設定変更が完了しました' }));
  } catch (e) {
    yield call(errorHandler, { error: e });
  } finally {
    yield put(changeOmakasePlanNameRequestEndLoading());
  }
}

function* getOmakasePlanMasterRequestHandler(action) {
  const {
    payload: { successCallback, failureCallback },
  } = action;
  try {
    yield put(getOmakasePlanMasterRequestStartLoading());

    const keys = Object.values(OmakasePlanTerms).flatMap((term) =>
      Object.values(OmakasePlanPolicies).map((policy) => `${term}-${policy}`),
    );
    const results = yield all(
      keys.map((key) =>
        call(function* fetchFiles() {
          const filePath = `${key}.json`;
          try {
            const { data } = yield call(fetchFileFromS3, { filePath });
            return [key, data];
          } catch (e) {
            return [key, e.message];
          }
        }),
      ),
    );

    yield put(
      getOmakasePlanMasterSuccess({
        omakasePlanMaster: Object.fromEntries(results),
      }),
    );
    successCallback?.();
  } catch (e) {
    yield call(errorHandler, { error: e });
    try {
      failureCallback?.();
    } catch (e2) {
      yield call(errorHandler, { error: e2 });
    }
  } finally {
    yield put(getOmakasePlanMasterRequestEndLoading());
  }
}

export default function* omakasePlanSagaHandler() {
  yield takeLatest(FIND_OMAKASE_PLAN_STRATEGIES, findOmakasePlanStrategiesHandler);
  yield takeLatest(CHANGE_CREATING_SIMULATION_TERM_ID, changeSimulationTermIdHandler);
  yield takeLatest(CREATE_OMAKASE_PLAN_REQUEST, createOmakasePlanRequestHandler);
  yield takeLatest(GET_PORTFOLIOS_REQUEST, getPortfoliosRequestHandler);
  yield takeLatest(GET_PORTFOLIO_DETAIL_REQUEST, getPortfolioDetailDataRequestHandler);
  yield takeEvery(CREATE_AP_GROUP_REQUEST, createOmakasePlanApGroupRequestHandler);
  yield takeEvery(CHANGE_NAME_REQUEST, changeOmakasePlanNameRequestHandler);
  yield takeLatest(GET_OMAKASE_PLAN_MASTER_REQUEST, getOmakasePlanMasterRequestHandler);
}
